import type { QuestionnaireResponse } from "@medplum/fhirtypes";

import isEqual from "lodash/isEqual";

import { readLaborAmendEncounterSavedFromSession } from "@/encounters/components/EncounterLabor/utils/labor-amend-session-state";
import {
  type EncounterAmendmentFormType,
  getEncounterInitialValues,
} from "@/encounters/zod-schemas/encounter-answers";

/** Whether the encounter QuestionnaireResponse is already persisted as amended on the server. */
export function isEncounterAmendmentSavedOnServer(
  questionnaireResponse: QuestionnaireResponse | undefined,
): boolean {
  return questionnaireResponse?.status === "amended";
}

/**
 * Whether Sign & Lock may skip the encounter amendment Medplum write.
 * Session storage alone is not sufficient; server `amended` status is required.
 */
export function shouldSkipEncounterAmendmentSave({
  encounterId,
  questionnaireResponse,
  inMemorySavedEncounterId,
}: {
  encounterId: string;
  questionnaireResponse: QuestionnaireResponse | undefined;
  /** Encounter id saved in-memory during this amend session; must match `encounterId` to skip. */
  inMemorySavedEncounterId: string | undefined;
}): boolean {
  if (inMemorySavedEncounterId === encounterId) {
    return true;
  }

  if (!readLaborAmendEncounterSavedFromSession(encounterId)) {
    return false;
  }

  return isEncounterAmendmentSavedOnServer(questionnaireResponse);
}

/** True when current amend form values differ from the loaded QuestionnaireResponse. */
export function hasEncounterAmendmentFormChanges(
  currentValues: EncounterAmendmentFormType,
  questionnaireResponse: QuestionnaireResponse | undefined,
): boolean {
  if (!questionnaireResponse) {
    return true;
  }

  const savedValues = getEncounterInitialValues(questionnaireResponse);
  return !isEqual(currentValues, savedValues);
}
